import type React from "react"
import { useCallback, useEffect, useMemo, useState } from "react"

import toast from "react-hot-toast"
import { Dropdown, type DropdownProps, Input } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { useThrottle } from "simplydo/react/hooks"
import styled from "styled-components"

import actions from "actions"
import api from "api"
import ProductTour from "components/lib/ProductTour"
import { useAppDispatch, useAppSelector } from "store"
import util from "utils/utils"

import CircularButtonWithNumber from "./CircularButtonWithNumber"
import { StyledNavBarSubSegment } from "./UI/NavBar"

const StyledDropdown = styled(Dropdown)`
  .menu.visible {
    right: -6px !important;
    margin: 10px 0 0 !important;
    width: 300px;
  }
`

const StyledDropdownItem = styled(Dropdown.Item)<{ $clickable?: boolean }>`
  ${({ $clickable }) =>
    $clickable
      ? `
    cursor: pointer;
    &:hover {
      background-color: #e9ebee;
    }
  `
      : ""}
  img {
    height: 30px;
    width: 30px !important;
    object-fit: contain;
  }
`

const OrgListItem = ({
  organisation,
  onClick,
  showCode,
  isRecent,
}: {
  organisation: OpenAPI.Schemas["Organisation"]
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  showCode?: boolean
  isRecent?: boolean
}) => (
  <StyledDropdownItem
    $clickable={!!onClick}
    onClick={onClick}
    image={isRecent ? null : organisation.darkLogoUrl}
    content={organisation.name}
    description={showCode ? organisation.code : null}
  />
)

type OrgSwitcherDropdownProps = {
  style?: React.CSSProperties
  pointing?: DropdownProps["pointing"]
  basic?: boolean
  trigger?: React.ReactNode
}

export const OrgSwitcherDropdown = ({ style, pointing = "top right", basic, trigger }: OrgSwitcherDropdownProps) => {
  const user = useAppSelector((state) => state.user)

  const [popupOpen, setPopupOpen] = useState(false)
  const [orgSearch, setOrgSearch] = useState("")
  const [orgLoading, setOrgLoading] = useState(false)
  const [recentOrgs, setRecentOrgs] = useState<OpenAPI.Schemas["Organisation"][]>([])
  const [orgs, setOrgs] = useState<OpenAPI.Schemas["Organisation"][]>([])
  const [superadminOrgSelectorVisible, setSuperadminOrgSelectorVisible] = useState(true)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (basic) return
    const recentOrgsLocalStorage = util.localStorageIsSupported()
      ? JSON.parse(localStorage.getItem("recentOrgs") || "[]")
      : []
    setRecentOrgs(recentOrgsLocalStorage.slice(0, 5))
  }, [basic])

  const fetchOrganisations = useThrottle(
    () => {
      setOrgLoading(true)
      api.organisations.getAll(
        {
          page: 1,
          query: orgSearch,
          selectedTypes: [],
          selectedTags: [],
        },
        (data) => {
          setOrgs(data.organisations)
          setOrgLoading(false)
        },
        (err) => {
          toast.error(err.message)
          setOrgLoading(false)
        },
      )
    },
    400,
    [orgSearch],
    { useLeadingCall: true },
  )

  useEffect(() => {
    if (popupOpen) {
      fetchOrganisations()
    }
  }, [fetchOrganisations, popupOpen])

  const switchOrganisation = useCallback(
    (orgId, addToRecents = true) => {
      api.users.switchOrganisation(
        user?._id,
        orgId,
        (data) => {
          if (util.localStorageIsSupported() && addToRecents) {
            // Recently switched organisations are stored in local storage, to a maximum of 5
            // Only add the organisation to the list if it's not already in the list
            const newRecentOrgs = recentOrgs.filter((org) => org._id !== orgId)
            newRecentOrgs.unshift(data.organisation)
            setRecentOrgs(newRecentOrgs.slice(0, 5))
            localStorage.setItem("recentOrgs", JSON.stringify(newRecentOrgs.slice(0, 5)))
          }

          dispatch(actions.user.switchOrganisation(data.organisation))
        },
        (err) => {
          toast.error(err.message)
        },
      )
    },
    [dispatch, recentOrgs, user?._id],
  )

  return (
    <StyledDropdown
      on="click"
      open={popupOpen}
      onClose={() => setPopupOpen(false)}
      icon={null}
      pointing={pointing}
      closeOnChange={false}
      trigger={
        <div
          role="button"
          onClick={() => setPopupOpen(true)}
          onKeyDown={(e) => e.key === "Enter" && setPopupOpen(true)}
          style={style}
        >
          {trigger ?? (
            <CircularButtonWithNumber
              image={util.orgFavicon(user.ownerOrganisation)}
              count={user?.ownerOrganisations?.length}
              style={style}
              labelProps={{
                color: "grey",
              }}
              className="org-switcher-tour-step-1"
              square
            />
          )}
        </div>
      }
    >
      <Dropdown.Menu>
        <Dropdown.Header content="Current Organisation" />
        <OrgListItem organisation={user.ownerOrganisation} showCode />
        {user?.ownerOrganisations?.length > 1 ||
        (user?.ownerOrganisations?.length === 1 && user.ownerOrganisations[0]._id !== user.ownerOrganisation._id) ? (
          <>
            <Dropdown.Header content="Switch Organisation" />
            {user.ownerOrganisations
              .filter((organisation) => organisation._id !== user.ownerOrganisation._id)
              .map((organisation) => (
                <OrgListItem
                  showCode
                  key={organisation._id}
                  organisation={organisation}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    switchOrganisation(organisation._id)
                  }}
                />
              ))}
          </>
        ) : null}
        {util.hasPermission(user, "super.switchOrg", user?.ownerOrganisation?._id) &&
        superadminOrgSelectorVisible &&
        !basic ? (
          <>
            <Dropdown.Divider />
            <Dropdown.Header content="Superadmin Org Selector" />
            <Dropdown.Item disabled>Recent orgs</Dropdown.Item>
            {recentOrgs.map((organisation) => (
              <OrgListItem
                key={organisation._id}
                organisation={organisation}
                isRecent
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  switchOrganisation(organisation._id, false)
                }}
              />
            ))}
            <Input
              value={orgSearch}
              onChange={(_e, { value }) => setOrgSearch(value)}
              onKeyDownCapture={(e) => {
                // The dropdown component stops propagation if space if pressed - if a key down happens in this components we always want to stop propagation here at the search
                if (e.key === " ") {
                  e.stopPropagation()
                }
              }}
              autoFocus
              placeholder="Search for org"
              onFocus={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              loading={orgLoading}
            />
            {orgs.map((organisation) => (
              <OrgListItem
                key={organisation._id}
                organisation={organisation}
                showCode
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  switchOrganisation(organisation._id)
                }}
              />
            ))}
            <Dropdown.Item
              content="Hide superadmin orgs this session"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setSuperadminOrgSelectorVisible(false)
              }}
            />
          </>
        ) : null}
      </Dropdown.Menu>
    </StyledDropdown>
  )
}

const OrgSwitcherTray = ({ style }: { style?: React.CSSProperties }) => {
  const user = useAppSelector((state) => state.user)

  const showOrgSwitcherTray = useMemo(
    () =>
      util.canAccessSuperadminOrgList(user) ||
      (user?.ownerOrganisations?.length && user?.ownerOrganisations?.length > 1),
    [user],
  )

  if (!user) {
    return null
  }
  if (!showOrgSwitcherTray) {
    return null
  }
  return (
    <>
      <ProductTour tour="orgSwitcher" isReady={!!user?.ownerOrganisations?.length} />
      <StyledNavBarSubSegment>
        <OrgSwitcherDropdown style={style} />
      </StyledNavBarSubSegment>
    </>
  )
}

export default OrgSwitcherTray
